// src/components/TipsCard.js

import React, { useState } from 'react';
import { Card, CardContent, Typography, Box, Divider, IconButton, useTheme, alpha } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate } from 'react-router-dom';
import { designTokens, commonCardStylesDark } from '../../../theme/designTokens';
import { bg, text as textColors, border, shadow, overlay } from '../../../utils/darkModeAdapter';

function TipsCard({ tipsData }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const [currentIndex, setCurrentIndex] = useState(0);

  const handleTipClick = (link) => {
    if (link) {
      navigate(link);
    }
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % tipsData.length);
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + tipsData.length) % tipsData.length);
  };

  const currentTip = tipsData[currentIndex];
  const tipText = typeof currentTip === 'string' ? currentTip : currentTip.text;
  const tipLink = typeof currentTip === 'object' ? currentTip.link : null;
  const isClickable = Boolean(tipLink);

  return (
    <Card
      component={motion.div}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      sx={{
        ...commonCardStylesDark(theme),
        mb: 3,
        overflow: 'visible',
        backgroundColor: '#FFEB3B0D',
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {/* タイトル */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              backgroundColor: alpha('#FDD835', 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 1.5,
            }}
          >
            <LightbulbIcon
              sx={{
                fontSize: 24,
                color: '#FDD835',
              }}
            />
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
              }}
            >
              おすすめのアクション
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
              }}
            >
              {currentIndex + 1} / {tipsData.length}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* カルーセル */}
        <Box sx={{ position: 'relative', minHeight: 120 }}>
          <AnimatePresence mode="wait">
            <Box
              key={currentIndex}
              component={motion.div}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.3 }}
              onClick={() => handleTipClick(tipLink)}
              sx={{
                p: 3,
                borderRadius: 2,
                background: `linear-gradient(135deg, ${alpha('#FDD835', 0.08)}, ${alpha('#FFF176', 0.05)})`,
                border: `2px solid ${alpha('#FDD835', 0.2)}`,
                position: 'relative',
                overflow: 'hidden',
                cursor: isClickable ? 'pointer' : 'default',
                transition: 'all 0.3s ease',
                '&:hover': isClickable
                  ? {
                      borderColor: alpha('#FDD835', 0.4),
                      boxShadow: `0 4px 16px ${alpha('#FDD835', 0.2)}`,
                      transform: 'translateY(-2px)',
                    }
                  : {},
              }}
            >

              <Box sx={{ pl: 2, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography
                    variant="body1"
                    sx={{
                      lineHeight: 1.7,
                      fontWeight: 500,
                      color: 'text.primary',
                    }}
                  >
                    {tipText}
                  </Typography>
                </Box>

                {isClickable && (
                  <IconButton
                    size="small"
                    sx={{
                      color: '#FDD835',
                      backgroundColor: alpha('#FDD835', 0.1),
                      '&:hover': {
                        backgroundColor: alpha('#FDD835', 0.2),
                      },
                    }}
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Box>
          </AnimatePresence>

          {/* ナビゲーションボタン */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 2,
              mt: 3,
            }}
          >
            <IconButton
              onClick={handlePrev}
              sx={{
                border: `1px solid ${border(theme).default}`,
                '&:hover': {
                  backgroundColor: alpha('#FDD835', 0.1),
                  borderColor: '#FDD835',
                },
              }}
            >
              <NavigateBeforeIcon />
            </IconButton>

            {/* インジケーター */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              {tipsData.map((_, index) => (
                <Box
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  sx={{
                    width: index === currentIndex ? 24 : 8,
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: index === currentIndex ? '#FDD835' : designTokens.colors.neutral[300],
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: index === currentIndex ? '#FDD835' : designTokens.colors.neutral[400],
                    },
                  }}
                />
              ))}
            </Box>

            <IconButton
              onClick={handleNext}
              sx={{
                border: `1px solid ${border(theme).default}`,
                '&:hover': {
                  backgroundColor: alpha('#FDD835', 0.1),
                  borderColor: '#FDD835',
                },
              }}
            >
              <NavigateNextIcon />
            </IconButton>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

export default TipsCard;
